/**
 * Perfil e regras deste exame (E151.F2, GUS-174, ADR-0103 §4.2), na mesa eletrônica.
 *
 * O modo vem do cadastro do caso (e a tela diz se é o padrão), o catálogo é o vigente com a versão,
 * e o perfil é o que a data do ato e a modalidade situam. As leituras que podem decidir vêm na ordem
 * do servidor; as regras preparadas aparecem com o motivo de não decidirem; o que impede a seleção
 * fica dito. O cliente não escolhe a norma pela interface, e nada aqui decide a conclusão.
 */
import type { AxiosInstance } from 'axios';

import { Blueprint, Button } from '@/components/ui';
import { ptBR } from '@/i18n/pt-BR';
import { dataBr, pendenciasPorCodigo, type PerfilDoExameData } from '@/lib/formalizacao';
import { usePerfilDoExame } from '@/lib/usePerfilDoExame';

import { LeiturasDoPerfil } from './LeiturasDoPerfil';

const t = ptBR.formalizacao;

function Linha({
  rotulo,
  valor,
  testid,
}: {
  rotulo: string;
  valor: string;
  testid: string;
}): JSX.Element {
  return (
    <div className="grid gap-sc-1 sm:grid-cols-[10rem_minmax(0,1fr)]">
      <dt className="text-body-sm text-fg-muted">{rotulo}</dt>
      <dd className="text-body-sm text-fg" data-testid={testid}>
        {valor}
      </dd>
    </div>
  );
}

function textoDoModo(dados: PerfilDoExameData): string {
  const { modo, padrao, definido_em: definidoEm } = dados.modo;
  const origem =
    padrao || definidoEm === null
      ? t.origemDoModo.padrao
      : t.origemDoModo.definido.replace('{data}', dataBr(definidoEm));
  return `${t.modo[modo]} (${origem})`;
}

function textoDaData(dados: PerfilDoExameData): string {
  const { datas, origem_das_datas: origem } = dados.selecao.contexto;
  const lidas = datas.map(dataBr).join(' ou ');
  return origem === 'adotada' ? lidas : `${lidas} (${t.origemDasDatas[origem]})`;
}

/** E162.F1.T12 (QA-10): marco do perfil ou regra de alguma leitura ainda não conferidos. */
function temNormaNaoConferida(dados: PerfilDoExameData): boolean {
  return dados.selecao.candidatos.some(
    (c) =>
      !c.inicio_conferido ||
      !c.fim_conferido ||
      c.leituras.some((l) => l.requisitos.some((r) => r.estado !== 'ativa')),
  );
}

function Pronto({ dados }: { dados: PerfilDoExameData }): JSX.Element {
  const { selecao } = dados;
  const catalogo = t.catalogo
    .replace('{versao}', selecao.catalogo_versao)
    .replace('{metodologia}', dados.metodologia);
  return (
    <>
      <dl className="space-y-sc-2">
        <Linha rotulo={t.rotulos.modo} valor={textoDoModo(dados)} testid="formalizacao-modo" />
        <Linha rotulo={t.rotulos.catalogo} valor={catalogo} testid="formalizacao-catalogo" />
        <Linha
          rotulo={t.rotulos.modalidade}
          valor={t.modalidade[selecao.contexto.modalidade]}
          testid="formalizacao-modalidade"
        />
        {selecao.contexto.datas.length > 0 ? (
          <Linha
            rotulo={t.rotulos.dataDoAto}
            valor={textoDaData(dados)}
            testid="formalizacao-data"
          />
        ) : null}
      </dl>
      {dados.aguardando_exame ? (
        <p className="text-body-sm text-fg-muted" data-testid="formalizacao-aguardando">
          {t.aguardandoExame}
        </p>
      ) : null}
      {selecao.candidatos.map((c) => (
        <LeiturasDoPerfil
          key={`${c.perfil_id}-${c.data}`}
          candidato={c}
          modo={selecao.contexto.modo}
        />
      ))}
      <section
        aria-labelledby="formalizacao-pendencias-titulo"
        data-testid="formalizacao-pendencias"
      >
        <h4 id="formalizacao-pendencias-titulo" className="text-body-md font-semibold text-fg">
          {t.pendencias}
        </h4>
        {selecao.pendencias.length === 0 ? (
          <p className="text-body-sm text-fg-muted">
            {temNormaNaoConferida(dados) ? t.semPendenciasComRessalva : t.semPendencias}
          </p>
        ) : (
          <ul className="list-disc pl-sc-6 text-body-sm text-fg">
            {pendenciasPorCodigo(selecao.pendencias).map((codigo) => (
              <li key={codigo} data-pendencia={codigo}>
                {t.pendencia[codigo]}
              </li>
            ))}
          </ul>
        )}
      </section>
    </>
  );
}

export interface PerfilDoExameProps {
  client: AxiosInstance;
  caseId: string;
  /** Muda quando o caso é relido (exame verificado de novo): o perfil é lido de novo. */
  releitura?: unknown;
}

export function PerfilDoExame({ client, caseId, releitura }: PerfilDoExameProps): JSX.Element {
  const { perfil, reler } = usePerfilDoExame(client, caseId, releitura);
  return (
    <Blueprint
      as="section"
      className="mt-sc-6 space-y-sc-4 p-sc-6"
      data-testid="perfil-do-exame"
      data-estado={perfil.estado}
      aria-labelledby="perfil-do-exame-titulo"
    >
      <div>
        <h3 id="perfil-do-exame-titulo" className="text-body-lg text-fg">
          {t.titulo}
        </h3>
        <p className="mt-1 max-w-prose text-body-sm text-fg-muted">{t.subtitulo}</p>
      </div>
      {perfil.estado === 'carregando' ? (
        <p className="text-body-sm text-fg-muted">{t.carregando}</p>
      ) : null}
      {perfil.estado === 'erro' ? (
        <div role="alert" className="space-y-sc-2" data-testid="perfil-do-exame-erro">
          <p className="text-body-sm text-danger-700">
            {perfil.motivo === 'sem_permissao'
              ? t.semPermissao
              : perfil.motivo === 'nao_encontrado'
                ? t.naoEncontrado
                : t.erro}
          </p>
          {perfil.motivo === 'indisponivel' ? (
            <Button type="button" variant="secondary" size="sm" onClick={reler}>
              {t.tentarDeNovo}
            </Button>
          ) : null}
        </div>
      ) : null}
      {perfil.estado === 'pronto' ? <Pronto dados={perfil.dados} /> : null}
    </Blueprint>
  );
}
